import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useCentralStore } from "../../../store/Central";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/dentlab/src/components/ui/table";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/dentlab/src/components/ui/card";
import { Info } from "lucide-react";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import { EmptyDataPlaceholder } from "@/components/src/empty-with-label/empty-data-placeholder.component";
import { Logger } from "@/lib/logger/Logger";

interface RueckverguetungRow {
	id: number;
	doc_number: string;
	value: number;
	date: string;
	currency: string;
	note_1: string | null;
	canceled_at: string | null;
	created_at: string;
	client_first_name: string | null;
	client_last_name: string | null;
	client_company_name: string | null;
	client_is_company: boolean | null;
	guarantor_first_name: string | null;
	guarantor_last_name: string | null;
	guarantor_company_name: string | null;
	guarantor_is_company: boolean | null;
	recipient_client_id: string | null;
	recipient_guarantor_id: string | null;
}

const formatCurrency = (amount: number, currency = "CHF") => {
	return `${amount.toFixed(2)} ${currency}`;
};

const formatDate = (dateString: string) => {
	return new Date(dateString).toLocaleDateString("de-CH");
};

const getRecipientName = (row: RueckverguetungRow): string => {
	if (row.recipient_guarantor_id) {
		if (row.guarantor_is_company && row.guarantor_company_name)
			return row.guarantor_company_name;
		return (
			[row.guarantor_first_name, row.guarantor_last_name]
				.filter(Boolean)
				.join(" ") || "–"
		);
	}
	if (row.client_is_company && row.client_company_name)
		return row.client_company_name;
	return (
		[row.client_first_name, row.client_last_name]
			.filter(Boolean)
			.join(" ") || "–"
	);
};

export const RueckverguetungPage: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState<RueckverguetungRow[]>([]);
	const organizationId = useCentralStore((s) => s.organization?.id);

	const fetchData = useCallback(async () => {
		if (!organizationId) return;
		setLoading(true);

		const { data: invoices, error } = await supabase
			.from("invoices")
			.select(
				`
				id, doc_number, value, date, currency, note_1, canceled_at, created_at,
				recipient_client_id, recipient_guarantor_id,
				client:clients!client_id(first_name, last_name, company_name, is_company),
				guarantor:guarantors!guarantor_id(first_name, last_name, company_name, is_company)
			`
			)
			.eq("organization_id", organizationId)
			.eq("type", 98)
			.order("date", { ascending: false });

		if (error) {
			Logger.error("Error fetching Rückvergütungen" + error);
			setLoading(false);
			return;
		}

		const rows: RueckverguetungRow[] = (invoices || []).map((inv: any) => ({
			id: inv.id,
			doc_number: inv.doc_number,
			value: inv.value,
			date: inv.date,
			currency: inv.currency || "CHF",
			note_1: inv.note_1,
			canceled_at: inv.canceled_at,
			created_at: inv.created_at,
			recipient_client_id: inv.recipient_client_id,
			recipient_guarantor_id: inv.recipient_guarantor_id,
			client_first_name: inv.client?.first_name ?? null,
			client_last_name: inv.client?.last_name ?? null,
			client_company_name: inv.client?.company_name ?? null,
			client_is_company: inv.client?.is_company ?? null,
			guarantor_first_name: inv.guarantor?.first_name ?? null,
			guarantor_last_name: inv.guarantor?.last_name ?? null,
			guarantor_company_name: inv.guarantor?.company_name ?? null,
			guarantor_is_company: inv.guarantor?.is_company ?? null,
		}));

		setData(rows);
		setLoading(false);
	}, [organizationId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const totalActive = data
		.filter((r) => !r.canceled_at)
		.reduce((sum, r) => sum + Math.abs(r.value), 0);

	return (
		<div className="py-6">
			<div className="px-6 flex justify-center">
				<Card className="w-fit">
					<CardHeader>
						<div className="flex items-center gap-2">
							<TooltipWrapper title="Rückvergütungen sind Auszahlungen von Auftraggeberguthaben (Akonto). Wenn ein Kunde ein offenes Guthaben hat und dieses zurückerstattet werden soll, wird eine Rückvergütung erstellt.">
								<button
									type="button"
									className="text-gray-400 hover:text-gray-600 p-1"
									aria-label="Zweck anzeigen"
								>
									<Info className="h-4 w-4" />
								</button>
							</TooltipWrapper>
							<CardTitle>Rückvergütungen</CardTitle>
							{data.length > 0 && (
								<span className="text-sm text-gray-500 ml-2">
									({data.filter((r) => !r.canceled_at).length}{" "}
									aktiv, Total: {formatCurrency(totalActive)})
								</span>
							)}
						</div>
					</CardHeader>
					<CardContent>
						{loading ? (
							<div className="flex justify-center items-center py-8">
								<div className="text-gray-500">
									Lade Rückvergütungen...
								</div>
							</div>
						) : data.length === 0 ? (
							<EmptyDataPlaceholder label="Keine Rückvergütungen vorhanden" />
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className="w-28">
											Beleg-Nr.
										</TableHead>
										<TableHead className="w-28">
											Datum
										</TableHead>
										<TableHead className="w-48">
											Empfänger
										</TableHead>
										<TableHead className="text-right w-32">
											Betrag
										</TableHead>
										<TableHead className="w-24">
											Währung
										</TableHead>
										<TableHead className="w-60">
											Notiz
										</TableHead>
										<TableHead className="w-24">
											Status
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.map((row) => (
										<TableRow
											key={row.id}
											className={
												row.canceled_at
													? "opacity-50"
													: ""
											}
										>
											<TableCell className="font-mono text-sm">
												{row.doc_number}
											</TableCell>
											<TableCell>
												{formatDate(row.date)}
											</TableCell>
											<TableCell>
												{getRecipientName(row)}
											</TableCell>
											<TableCell className="text-right font-mono">
												{formatCurrency(
													Math.abs(row.value)
												)}
											</TableCell>
											<TableCell>
												{row.currency}
											</TableCell>
											<TableCell className="text-sm text-gray-600 truncate max-w-60">
												{row.note_1 || "–"}
											</TableCell>
											<TableCell>
												{row.canceled_at ? (
													<span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-600">
														Storniert
													</span>
												) : (
													<span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-600">
														Aktiv
													</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</CardContent>
				</Card>
			</div>
		</div>
	);
};
